import { useNavigate } from 'react-router-dom';
import { Button } from '@design-system/Button';
import { Link } from '@design-system/Link';
import { ChevronRightIcon } from '@/components/icons/ChevronRightIcon';
import { ScoreCard } from '@design-system/Cards/ScoreCard';
import { Skeleton } from '@design-system/Skeleton';
import { getRankColor } from '@/utils/getViomeColor';
import { HEALTH_CONTRIBUTING_SCORE } from '@/constants/paths';
import { useGetContributingScoresV2 } from '@viome/viome-base/src/usecase/Health/HealthQuery';

interface ScoreListProps {
  title: string;
  description: string;
}

export const ScoreListSection: React.FC<ScoreListProps> = ({
  title,
  description,
}) => {
  const navigate = useNavigate();

  const { data, isLoading } = useGetContributingScoresV2('L2');

  const navigateToConditionsPage = (id: number) => {
    return navigate(`/health/l3/${id}`);
  };

  const scores = data?.filter((score) => score.score_type === 'L3') || [];

  if (isLoading || !data) {
    // Skeleton View
    return (
      <div>
        <Skeleton className="h-9 w-[50%] mb-4 rounded-lg" />
        <Skeleton className="h-6 w-[70%] mb-6 rounded-lg" />
        <div className="grid lg:grid-cols-2 gap-2.5 md:grid-cols-1 mb-6">
          {Array.from({ length: 4 }).map((_, index) => (
            <Skeleton
              key={`skeleton-card-${index}`}
              className="h-28 w-full rounded-lg"
            />
          ))}
        </div>
        <Skeleton className="h-[48px] w-full rounded-lg" />
      </div>
    );
  }

  return (
    <div data-testid="health-scoreList-section">
      <h2 className="dark typography-h1 text-primary mb-2" data-testid="health-scoreList-title">{title}</h2>
      <p className="dark typography-body text-secondary mb-6 lg:w-[35rem]" data-testid="health-scoreList-description">
        {description}
      </p>
      <div className="grid lg:grid-cols-2 gap-2.5 md:grid-cols-1 mb-6" data-testid="health-scoreList-grid">
        {scores.map((zone) => (
          <div key={`${zone.title}-${zone.id}`} data-testid={`health-scoreList-card-${zone.title.replace(/\s+/g, '-').toLowerCase()}`}>
            <ScoreCard
              title={zone.title}
              tag={zone.rank}
              color={getRankColor(zone.rank)}
              score={zone.scores}
              variant="dark"
              hasProgressBar
              data-testid={`health-scoreList-scoreCard-${zone.title.replace(/\s+/g, '-').toLowerCase()}`}
              onClick={() => {
                navigateToConditionsPage(zone.id);
              }}
            />
          </div>
        ))}
      </div>
      <Button
        asChild
        variant="secondary"
        className="w-full h-12 [&_svg]:!size-6 justify-between pr-3 mt-5"
        data-testid="health-scoreList-seeAllButton"
      >
        <Link to={HEALTH_CONTRIBUTING_SCORE}>
          See all scores
          <ChevronRightIcon height={24} width={24} />
        </Link>
      </Button>
    </div>
  );
};
